import { useState, useRef } from "react"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { createOrg, updateOrg, uploadOrgLogo, orgLogoUrl } from "./api"

function LogoButton({ previewUrl, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 group border-2 border-dashed border-bordered hover:border-primary transition-colors"
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="org logo"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-canvas">
          <svg
            className="w-6 h-6 text-text_secondary"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 21h18"
            />
          </svg>
        </div>
      )}

      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <svg
          className="w-5 h-5 text-text_inv"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
          />
        </svg>
      </div>
    </button>
  );
}

export default function CreateOrgDialog({ org, onClose, onSaved }) {
  const isEdit = !!org;
  const [name, setName] = useState(org?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);

  const [orgId, setOrgId] = useState(org?._id ?? null);
  const [logoKey, setLogoKey] = useState(org?.logo ?? null);

  const pendingLogoRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(
    org?.logo && org?._id ? orgLogoUrl(org._id) : null,
  );

  const texts = TextBundle({ id: "aldoo_ra_organizations" });

  function makeUploadFn(resolvedOrgId) {
    return async (blob, mimeType) => {
      setPreviewUrl(URL.createObjectURL(blob));
      const res = await uploadOrgLogo(resolvedOrgId, blob, mimeType);
      if (res?.result) {
        setLogoKey(res.logo);
        setPreviewUrl(`${orgLogoUrl(resolvedOrgId)}?t=${Date.now()}`);
      }
      return res;
    };
  }

  function pendingUploadFn(blob, mimeType) {
    pendingLogoRef.current = { blob, mimeType };
    setPreviewUrl(URL.createObjectURL(blob));
    return Promise.resolve({ result: true });
  }

  async function handleSave() {
    if (!name.trim()) {
      setError(texts?.name_required);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let res;
      if (isEdit) {
        res = await updateOrg(org._id, name);
        if (res.error) {
          setError(res.error);
          return;
        }
        onSaved?.(res.org);
        onClose();
      } else {
        res = await createOrg(name);
        if (res.error) {
          setError(res.error);
          return;
        }
        const newOrgId = res.org._id;
        setOrgId(newOrgId);

        if (pendingLogoRef.current) {
          const { blob, mimeType } = pendingLogoRef.current;
          await uploadOrgLogo(newOrgId, blob, mimeType);
          pendingLogoRef.current = null;
        }

        onSaved?.(res.org);
        onClose();
      }
    } catch {
      setError(texts?.save_failed);
    } finally {
      setSaving(false);
    }
  }

  const uploadFn = orgId ? makeUploadFn(orgId) : pendingUploadFn;

  if (!texts) return null;

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        variant="centered"
        className="md:max-w-md"
        title={isEdit ? texts?.edit_org_title : texts?.create_org_title}
      >
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <LogoButton
              previewUrl={previewUrl}
              onClick={() => setLogoDialogOpen(true)}
            />

            <div className="flex-1 space-y-1">
              <label className="text-sm font-medium text-text_secondary pl-3">
                {texts?.name_label}
              </label>

              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                placeholder={texts?.org_name_placeholder}
                className=""
              />
            </div>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}

          <div className="flex gap-3 pt-1">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              {texts?.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              className="flex-1"
            >
              {saving ? texts?.saving : isEdit ? texts?.save_changes : texts?.create}
            </Button>
          </div>
        </div>
      </Modal>

      {logoDialogOpen && (
        <AddMediaDialog
          imagesOnly
          currentImageUrl={previewUrl}
          uploadFn={uploadFn}
          onUse={(result) => {
            setLogoDialogOpen(false);
            if (result?.logo) setLogoKey(result.logo);
          }}
          onCancel={() => setLogoDialogOpen(false)}
        />
      )}
    </>
  );
}